import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  IconButton,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Divider,
  InputAdornment,
  styled,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DevicesIcon from "@mui/icons-material/Devices";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AppleIcon from "@mui/icons-material/Apple";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CodeIcon from "@mui/icons-material/Code";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";


const StyledButton = styled(Button)`
  padding: 12px 24px;
  border-radius: 8px;
  text-transform: capitalize;
  font-weight: 500;
`;

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  marginBottom: theme.spacing(1),
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.03)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#eef1f8",
    borderColor: "rgba(37, 80, 209, 0.1)",
  },
}));

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background-color: #ffffff;
    
    &:focus-within {
      border-color: #2550d1;
      box-shadow: 0 0 0 2px rgba(37, 80, 209, 0.1);
    }
  }
`;

const FormField = ({ label, tooltip, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography color="#666" fontSize="14px" fontWeight={500}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);

const CapabilitiesManager = ({ 
  savedCapabilities = [], 
  onSaveCapability, 
  onError, 
  onSuccess,
  isSaving = false 
}) => {

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputMode, setInputMode] = useState("form");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [capabilityName, setCapabilityName] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState("");

  const defaultCapabilities = {
    platformName: "",
    platformVersion: "",
    deviceName: "",
    automationName: "",
  };

  const [capabilities, setCapabilities] = useState(defaultCapabilities);
  useEffect(() => {
    if (selectedPlatform === "Android") {
      handleCapabilityChange("automationName", "UiAutomator2");
      handleCapabilityChange("platformName", "Android");
    } else if (selectedPlatform === "iOS") {
      handleCapabilityChange("automationName", "XCUITest");
      handleCapabilityChange("platformName", "iOS");
    }
  }, [selectedPlatform]);

  const handleCapabilityChange = (key, value) => {
    setHasUnsavedChanges(true);
    setCapabilities(prev => ({ ...prev, [key]: value }));

    if (inputMode === "json") {
      const updated = { ...capabilities, [key]: value };
      setJsonInput(JSON.stringify(updated, null, 2));
    }
  };

  const handleJsonInput = (value) => {
    setJsonInput(value);
    setJsonError("");
    setHasUnsavedChanges(true);

    try {
      const parsed = JSON.parse(value);
      const requiredFields = ["platformName", "platformVersion", "deviceName", "automationName"];
      const missingFields = requiredFields.filter(field => !parsed[field]);
      
      if (missingFields.length > 0) {
        setJsonError(`Missing required fields: ${missingFields.join(", ")}`);
        return;
      }

      setCapabilities({ ...defaultCapabilities, ...parsed });
      
      if (parsed.platformName === "Android") {
        setSelectedPlatform("Android");
      } else if (parsed.platformName === "iOS") {
        setSelectedPlatform("iOS");
      }
    } catch (e) {
      setJsonError("Invalid JSON format");
    }
  };

  const handleSave = async () => {
    if (!capabilityName.trim()) {
      onError?.("Please enter a name for the capability");
      return;
    }

    try {
      await onSaveCapability?.({
        name: capabilityName,
        capabilities: capabilities,
      });
      
      resetForm();
      setIsModalOpen(false);
      setSaveDialogOpen(false);
      onSuccess?.("Capability saved successfully!");
    } catch (error) {
      onError?.("Failed to save capability: " + (error.message || "Unknown error"));
    }
  };

  const resetForm = () => {
    setCapabilities(defaultCapabilities);
    setSelectedPlatform("");
    setCapabilityName("");
    setJsonInput("");
    setJsonError("");
    setHasUnsavedChanges(false);
  };

  const handleCloseModal = () => {
    if (hasUnsavedChanges) {
      setSaveDialogOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };

  const handleDiscardChanges = () => {
    resetForm();
    setIsModalOpen(false);
    setSaveDialogOpen(false);
  };

  const renderPlatformSelection = () => (
    <FormField label="Select Platform*">
      <Grid container spacing={2}>
        {["Android", "iOS"].map((platform) => (
          <Grid item xs={12} sm={6} key={platform}>
            <Button
              fullWidth
              variant={selectedPlatform === platform ? "contained" : "outlined"}
              onClick={() => setSelectedPlatform(platform)}
              sx={{
                padding: 2,
                borderRadius: "8px",
                border: `1px solid ${selectedPlatform === platform ? "#2550d1" : "#e0e0e0"}`,
                backgroundColor: selectedPlatform === platform ? "#2550d1" : "#ffffff",
                color: selectedPlatform === platform ? "#ffffff" : "#333",
                height: "100px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: selectedPlatform === platform ? "#1e3fa1" : "#f5f5f5",
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {platform === "Android" ? (
                  <PhoneAndroidIcon sx={{ fontSize: 32, mb: 1 }} />
                ) : (
                  <AppleIcon sx={{ fontSize: 32, mb: 1 }} />
                )}
                <Typography variant="subtitle1" fontWeight={500}>
                  {platform}
                </Typography>
              </Box>
            </Button>
          </Grid>
        ))}
      </Grid>
    </FormField>
  );

  const renderFormFields = () => (
    <Box>
      {renderPlatformSelection()}
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#333" }}>
        Device Configuration
      </Typography>

      <FormField label="Platform Version*">
        <StyledTextField
          fullWidth
          value={capabilities.platformVersion}
          onChange={(e) => handleCapabilityChange("platformVersion", e.target.value)}
          placeholder={selectedPlatform === "Android" ? "E.g. 13.0" : "E.g. 16.2"}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DevicesIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </FormField>

      <FormField label="Device Name*">
        <StyledTextField
          fullWidth
          value={capabilities.deviceName}
          onChange={(e) => handleCapabilityChange("deviceName", e.target.value)}
          placeholder={selectedPlatform === "Android" ? "E.g. emulator-5554" : "E.g. iPhone Simulator"}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {selectedPlatform === "Android" ? <PhoneAndroidIcon fontSize="small" /> : <AppleIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />
      </FormField>

      <FormField
        label="Automation Name"
        tooltip={`Auto-set based on platform: ${selectedPlatform === "Android" ? "UiAutomator2" : "XCUITest"}`}
      >
        <StyledTextField
          fullWidth
          value={capabilities.automationName}
          disabled
          variant="outlined"
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f5f5f5" } }}
        />
      </FormField>
    </Box>
  );

  const renderJsonInput = () => (
    <Box width="100%">
      <StyledTextField
        multiline
        rows={10}
        fullWidth
        value={jsonInput}
        onChange={(e) => handleJsonInput(e.target.value)}
        error={!!jsonError}
        helperText={jsonError}
        placeholder="Paste your capabilities JSON here"
        variant="outlined"
        sx={{ fontFamily: "monospace", "& .MuiInputBase-input": { fontFamily: "monospace" } }}
      />
      <Box sx={{ mt: 2, p: 2, backgroundColor: "#f8f9fc", borderRadius: "8px" }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
          Example Format:
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 1, fontFamily: "monospace" }}>
          {`{
  "platformName": "${selectedPlatform || "Android"}",
  "platformVersion": "11",
  "deviceName": "${selectedPlatform === "Android" ? "emulator-5554" : "iPhone Simulator"}",
  "automationName": "${selectedPlatform === "Android" ? "UiAutomator2" : "XCUITest"}"
}`}
        </Typography>
      </Box>
    </Box>
  );

  const renderSavedCapabilities = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
          Saved Configurations
        </Typography>
        <StyledButton
          onClick={() => setIsModalOpen(true)}
          startIcon={<AddIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#2550d1",
            "&:hover": { backgroundColor: "#1e3fa1" },
          }}
        >
          Create New
        </StyledButton>
      </Box>

      {savedCapabilities?.length > 0 ? (
        <List sx={{ px: 0 }}>
          {savedCapabilities.map((capability, index) => {
            const capData = capability.capability || {};
            return (
              <StyledListItem key={index}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={600}>
                      {capability.name || `Configuration ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        icon={capData.platformName === "Android" ? <PhoneAndroidIcon fontSize="small" /> : <AppleIcon fontSize="small" />}
                        label={capData.platformName || "Not specified"}
                        sx={{
                          mr: 1, mb: 1,
                          backgroundColor: capData.platformName === "Android" ? "rgba(76, 175, 80, 0.1)" : "rgba(33, 150, 243, 0.1)",
                          color: capData.platformName === "Android" ? "#388e3c" : "#1976d2",
                          borderRadius: "6px",
                        }}
                      />
                      <Chip size="small" label={`Device: ${capData.deviceName || "Not specified"}`} sx={{ mr: 1, mb: 1, borderRadius: "6px" }} />
                      <Chip size="small" label={`Version: ${capData.platformVersion || "Not specified"}`} sx={{ mr: 1, mb: 1, borderRadius: "6px" }} />
                    </Box>
                  }
                />
              </StyledListItem>
            );
          })}
        </List>
      ) : (
        <Box sx={{ textAlign: "center", py: 4, backgroundColor: "#f8f9fc", borderRadius: "8px" }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
            No saved configurations
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 400, color: "text.secondary" }}>
            Create and save device configurations to reuse them in future test sessions
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {renderSavedCapabilities()}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "85vh",
          overflow: "hidden",
          margin: "20px",
          boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.1)",
        }}>
 
          <Box sx={{ p: 2.5, backgroundColor: "#f8f9fc", borderBottom: "1px solid rgba(0, 0, 0, 0.06)" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <DevicesIcon sx={{ color: "#2550d1", mr: 1, fontSize: 24 }} />
                <Typography variant="h6" fontWeight={600}>Create Device Capability</Typography>
              </Box>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>


          <Box sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.06)" }}>
            <Tabs
              value={inputMode}
              onChange={(_, newMode) => setInputMode(newMode)}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontSize: "0.9rem", fontWeight: 500, py: 1.5 },
                "& .Mui-selected": { color: "#2550d1" },
                "& .MuiTabs-indicator": { backgroundColor: "#2550d1", height: 2 },
              }}
            >
              <Tab label="Form Input" value="form" icon={<SettingsIcon />} iconPosition="start" />
              <Tab label="JSON Input" value="json" icon={<CodeIcon />} iconPosition="start" />
            </Tabs>
          </Box>


          <Box sx={{ p: 2.5, maxHeight: "calc(85vh - 120px)", overflow: "auto" }}>
            {inputMode === "form" ? renderFormFields() : renderJsonInput()}
          </Box>


          <Box sx={{
            p: 2, px: 2.5,
            display: "flex", justifyContent: "flex-end", gap: 1.5,
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
            backgroundColor: "#f8f9fc",
          }}>
            <StyledButton onClick={handleDiscardChanges} variant="outlined">Cancel</StyledButton>
            {hasUnsavedChanges && (
              <StyledButton
                onClick={() => setSaveDialogOpen(true)}
                disabled={(inputMode === "json" && !!jsonError) || isSaving}
                startIcon={<SaveIcon />}
                variant="contained"
                sx={{ backgroundColor: "#2550d1", "&:hover": { backgroundColor: "#1e3fa1" } }}
              >
                {isSaving ? "Saving..." : "Save Configuration"}
              </StyledButton>
            )}
          </Box>
        </Box>
      </Modal>


      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} PaperProps={{ sx: { borderRadius: "12px", maxWidth: "450px" } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pt: 2.5 }}>Save Device Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save this configuration to reuse it in future test sessions.
          </Typography>
          <StyledTextField
            autoFocus
            margin="dense"
            label="Configuration Name"
            fullWidth
            value={capabilityName}
            onChange={(e) => setCapabilityName(e.target.value)}
            placeholder="Enter a name for this configuration"
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleDiscardChanges} sx={{ color: "text.secondary", fontWeight: 500, textTransform: "none", borderRadius: "6px" }}>
            Cancel
          </Button>
          <StyledButton
            onClick={handleSave}
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={!capabilityName.trim() || isSaving}
            sx={{ backgroundColor: "#2550d1", "&:hover": { backgroundColor: "#1e3fa1" } }}
          >
            {isSaving ? "Saving..." : "Save Configuration"}
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CapabilitiesManager;